import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useMe } from '../../_shared/hooks/useMe';
import { findAnchorTarget } from '../../_shared/lib/findAnchorTarget';
import { highlightAnchorTarget } from '../../_shared/lib/highlightAnchorTarget';
import { useActiveStandards } from '../hooks/useActiveStandards';
import { useStandardsVersion } from '../hooks/useStandardsVersion';
import { useStandardsList } from '../hooks/useStandardsList';
import { StandardsToc } from '../components/StandardsToc';
import { StandardsSearchBar } from '../components/StandardsSearchBar';
import { MarkdownView } from '../components/MarkdownView';
import { StandardsDownloadButtons } from '../components/StandardsDownloadButtons';
import { UploadStandardsDialog } from '../components/UploadStandardsDialog';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { StandardsDiffModal } from '../components/StandardsDiffModal';
import { resolveExportedByDisplay } from '../lib/resolveExportedByDisplay';
import type { ApiError } from '../types/devStandards';

/* ─── Skeleton ───────────────────────────────────────────────────────────── */

function SkeletonBlock({ height }: { height: number | string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        background: 'var(--color-surface-muted)',
        borderRadius: 'var(--radius-md)',
        height,
        animation: 'pulse 1.5s ease-in-out infinite',
        marginBottom: 'var(--spacing-3)',
      }}
    />
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ display: 'flex', gap: 'var(--spacing-6)', padding: 'var(--spacing-6)' }}>
      {/* TOC skeleton */}
      <div
        aria-busy="true"
        style={{ minWidth: 200, borderRight: '1px solid var(--color-surface-border)', paddingRight: 'var(--spacing-4)' }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonBlock key={i} height={16} />
        ))}
      </div>
      {/* Body skeleton */}
      <div style={{ flex: 1 }}>
        <SkeletonBlock height={40} />
        <SkeletonBlock height={24} />
        <SkeletonBlock height={200} />
        <SkeletonBlock height={24} />
        <SkeletonBlock height={150} />
      </div>
    </div>
  );
}

/* ─── Meta bar ────────────────────────────────────────────────────────────── */

function DocMetaBar({
  docVersion,
  sourceCommit,
  exportedAt,
  exportedBy,
  exportedByDisplay,
}: {
  docVersion: string;
  sourceCommit: string | null;
  exportedAt: string;
  exportedBy: string;
  exportedByDisplay?: string;
}) {
  const { t, i18n } = useTranslation();
  const dateStr = new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(exportedAt));

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-3)',
        flexWrap: 'wrap',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-muted)',
        fontFamily: 'var(--font-family-mono)',
      }}
    >
      <span>
        <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)' }}>
          {t('devStandardsViewer.meta.version')}:
        </span>{' '}
        {docVersion}
      </span>
      {sourceCommit && (
        <span>
          <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)' }}>
            {t('devStandardsViewer.meta.commit')}:
          </span>{' '}
          {sourceCommit.slice(0, 8)}
        </span>
      )}
      <span>
        <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)' }}>
          {t('devStandardsViewer.meta.exportedAt')}:
        </span>{' '}
        {dateStr}
      </span>
      <span>
        <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)' }}>
          {t('devStandardsViewer.meta.exportedBy')}:
        </span>{' '}
        {resolveExportedByDisplay(exportedBy, exportedByDisplay)}
      </span>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */

export function DevStandardsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const highlightCleanupRef = useRef<(() => void) | null>(null);
  const [searchParams] = useSearchParams();
  const versionParam = searchParams.get('version');
  const versionId = versionParam ? parseInt(versionParam, 10) : null;

  const meQuery = useMe();
  const isAdmin = meQuery.data?.role === 'ROLE_ADMIN';

  const activeQuery = useActiveStandards();
  const versionQuery = useStandardsVersion(versionId);

  // Version list for diff modal + degraded-fallback navigation (top 20)
  const versionListQuery = useStandardsList(0, 20);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [successToast, setSuccessToast] = useState(false);

  // Diff modal state: null = closed; number = the "compare" version id
  const [diffCompareId, setDiffCompareId] = useState<number | null>(null);

  const handleUploadSuccess = useCallback(() => {
    setSuccessToast(true);
    setTimeout(() => setSuccessToast(false), 4000);
  }, []);

  // Decide which document to render
  const displayDoc = versionId != null ? versionQuery.data : activeQuery.data;
  const isLoading =
    versionId != null ? versionQuery.isLoading : activeQuery.isLoading;
  const isError =
    versionId != null ? versionQuery.isError : activeQuery.isError;
  const errorObj = (versionId != null ? versionQuery.error : activeQuery.error) as ApiError | null;
  const isEmpty = versionId == null && activeQuery.isEmpty;

  const isViewingOldVersion = versionId != null && displayDoc != null;

  /**
   * Degraded-content detection (#402 part 3).
   * Applies both to the active document and to any old version being viewed.
   * A document is considered degraded when its markdownText is blank or shorter
   * than 2000 characters — matching the BE upload validation threshold.
   */
  const isDegraded =
    displayDoc != null &&
    (!displayDoc.markdownText?.trim() || displayDoc.markdownText.trim().length < 2000);

  /**
   * queue #689 §UAT-2026-07-14-R689 §R689-1-⑦ — 통합검색 '개발표준' 그룹 hit 은
   * `/standards#<slug>` 로 이동한다. 문서 본문(`MarkdownView` · rehype-slug)이 렌더된 뒤에만
   * 대상 heading 이 DOM 에 존재하므로, 로딩 완료 + 정상(비저하) 콘텐츠일 때만 스크롤·하이라이트한다
   * (§R689-1-⑦ ConsumerGuidePage 와 동일 `highlightAnchorTarget` 재사용 — 재구현 0).
   */
  useEffect(() => {
    if (!location.hash || isLoading || isError || !displayDoc || isDegraded) return undefined;
    const id = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
    if (!id) return undefined;

    const frame = requestAnimationFrame(() => {
      // #696 — MarkdownView 는 sanitize clobberPrefix 로 heading id 를 user-content-* 로 바꾼다.
      const el = findAnchorTarget(id);
      el?.scrollIntoView({ block: 'start' });
      highlightCleanupRef.current?.();
      highlightCleanupRef.current = highlightAnchorTarget(el);
    });
    return () => {
      cancelAnimationFrame(frame);
      highlightCleanupRef.current?.();
      highlightCleanupRef.current = null;
    };
  }, [location.hash, isLoading, isError, displayDoc, isDegraded]);

  /**
   * Find the most recent prior version id for the "view previous" button.
   * The version list is sorted by exportedAt DESC; pick the first item whose
   * id differs from the currently displayed document.
   */
  const priorVersionId: number | null =
    isDegraded && versionListQuery.data?.content
      ? (versionListQuery.data.content.find((v) => v.id !== displayDoc?.id)?.id ?? null)
      : null;

  // Active document id (the is_active=true row) for diff "from/to" wiring
  const activeDocId = activeQuery.data?.id ?? null;

  /**
   * Diff modal wiring:
   * - "compareId" is the non-active version the user clicked "비교" on.
   * - We always diff: from=compareId (older) → to=activeDocId (newer).
   * - If compareId IS the active doc (edge case), or activeDocId is null, skip.
   */
  const handleDiffRequest = useCallback((compareId: number) => {
    setDiffCompareId(compareId);
  }, []);

  const closeDiff = useCallback(() => setDiffCompareId(null), []);

  // Build diff from/to ids
  const diffFromId =
    diffCompareId != null && activeDocId != null && diffCompareId !== activeDocId
      ? diffCompareId
      : diffCompareId; // fallback: compare against itself (modal shows "identical")
  const diffToId = activeDocId ?? diffCompareId ?? 0;

  const showDiffModal =
    diffCompareId != null && diffFromId != null && diffToId != null && diffToId !== 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Success toast */}
      {successToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 9999,
            background: 'var(--color-success)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family-sans)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.16)',
          }}
        >
          {t('devStandardsViewer.upload.success')}
        </div>
      )}

      {/* Page header */}
      <PageHeader
        title={t('devStandardsViewer.title')}
        subtitle={undefined}
        breadcrumb={
          <span>
            {t('common.nav.home')} / {t('devStandardsViewer.title')}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Meta info */}
            {displayDoc && (
              <DocMetaBar
                docVersion={displayDoc.docVersion}
                sourceCommit={displayDoc.sourceCommit}
                exportedAt={displayDoc.exportedAt}
                exportedBy={displayDoc.exportedBy}
                exportedByDisplay={displayDoc.exportedByDisplay}
              />
            )}
            {/* Downloads — only when doc available */}
            {displayDoc && (
              <StandardsDownloadButtons doc={displayDoc} markdownContainerId="standards-markdown-view" />
            )}
            {/* Admin upload button */}
            {isAdmin && (
              <button
                type="button"
                data-testid="upload-standards-btn"
                onClick={() => setDialogOpen(true)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family-sans)',
                  fontWeight: 600,
                }}
              >
                {t('devStandardsViewer.upload.button')}
              </button>
            )}
          </div>
        }
      />

      {/* 이전 버전 열람 알림 */}
      {isViewingOldVersion && (
        <div
          role="status"
          style={{
            background: 'var(--color-warning)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-2) var(--spacing-6)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('devStandardsViewer.version.viewing', { docVersion: displayDoc?.docVersion ?? versionId })}
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && <LoadingSkeleton />}

      {/* Error state */}
      {!isLoading && isError && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <ErrorState
            title={t('devStandardsViewer.error.title')}
            message={errorObj?.message ?? t('devStandardsViewer.error.message')}
            onRetry={() => {
              if (versionId != null) void versionQuery.refetch();
              else void activeQuery.refetch();
            }}
          />
        </div>
      )}

      {/* Empty state — 활성 문서 없음 */}
      {!isLoading && !isError && isEmpty && (
        <div style={{ padding: 'var(--spacing-8)' }}>
          <EmptyState
            title={t('devStandardsViewer.empty.title')}
            description={t('devStandardsViewer.empty.description')}
            action={
              isAdmin ? (
                <button
                  type="button"
                  data-testid="empty-upload-btn"
                  onClick={() => setDialogOpen(true)}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    fontWeight: 600,
                  }}
                >
                  {t('devStandardsViewer.empty.cta')}
                </button>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Degraded-content state — active or old version has empty/too-short body (#402) */}
      {!isLoading && !isError && displayDoc && isDegraded && (
        <div style={{ padding: 'var(--spacing-8)' }}>
          <EmptyState
            title={t('devStandardsViewer.degraded.title')}
            description={t('devStandardsViewer.degraded.description')}
            action={
              priorVersionId != null ? (
                <button
                  type="button"
                  data-testid="degraded-view-previous-btn"
                  onClick={() => void navigate(`?version=${priorVersionId}`)}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    fontWeight: 600,
                  }}
                >
                  {t('devStandardsViewer.degraded.viewPrevious')}
                </button>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Default: document rendered (only when content is not degraded) */}
      {!isLoading && !isError && displayDoc && !isDegraded && (
        <div
          style={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Left TOC — hidden on mobile (< 768px) */}
          <div
            style={{ display: 'flex' }}
            className="standards-toc-wrapper"
          >
            <StandardsToc markdownText={displayDoc.markdownText} />
          </div>

          {/* Center content */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              padding: 'var(--spacing-6)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-4)',
            }}
          >
            <StandardsSearchBar markdownText={displayDoc.markdownText} />
            <MarkdownView
              markdownText={displayDoc.markdownText}
              contentId="standards-markdown-view"
            />
          </div>

          {/* Right version history panel */}
          <VersionHistoryPanel
            currentVersionId={displayDoc.id}
            onDiffRequest={handleDiffRequest}
            activeDocId={activeDocId}
          />
        </div>
      )}

      {/* Upload dialog (dialog state) */}
      {dialogOpen && (
        <UploadStandardsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Diff modal */}
      {showDiffModal && (
        <StandardsDiffModal
          fromId={diffFromId!}
          toId={diffToId}
          versions={versionListQuery.data?.content ?? []}
          onClose={closeDiff}
        />
      )}

      {/* Responsive + animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @media (max-width: 768px) {
          .standards-toc-wrapper {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
